<script setup lang="ts">
import { computed, inject, shallowRef } from 'vue'
import { websiteEditableContextKey } from './editable-context'

const props = withDefaults(defineProps<{
  modelValue?: string
  as?: string
}>(), {
  modelValue: '',
  as: 'span',
})

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
const editor = inject(websiteEditableContextKey, null)
const editable = computed(() => editor?.editable.value === true)
const originalValue = shallowRef('')

function beginEditing(event: FocusEvent): void {
  originalValue.value = (event.currentTarget as HTMLElement).textContent ?? props.modelValue
}

function commit(event: FocusEvent | KeyboardEvent): void {
  const element = event.currentTarget as HTMLElement
  const value = (element.textContent ?? '').replace(/\u00a0/g, ' ').trim()

  if (value !== props.modelValue) {
    emit('update:modelValue', value)
    editor?.contentChanged()
  }
}

function cancel(event: KeyboardEvent): void {
  const element = event.currentTarget as HTMLElement
  element.textContent = originalValue.value
  element.blur()
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    event.preventDefault()
    const element = event.currentTarget as HTMLElement
    element.blur()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    cancel(event)
  }
}

function preventNavigation(event: MouseEvent): void {
  if (editable.value) event.preventDefault()
}
</script>

<template>
  <component
    :is="as"
    :contenteditable="editable ? 'plaintext-only' : undefined"
    :data-site-inline-editable="editable || undefined"
    :role="editable ? 'textbox' : undefined"
    :spellcheck="editable || undefined"
    :tabindex="editable ? 0 : undefined"
    @click="preventNavigation"
    @focus="beginEditing"
    @blur="commit"
    @keydown="handleKeydown"
  >{{ modelValue }}</component>
</template>
